import { ErrorCode, LogLevel, Offering, PackageType, Purchases, PurchasesError } from '@revenuecat/purchases-js';
import {
    EntitlementId,
    normalizeCustomer,
    OfferingId,
    PackageFrequency,
    PackageSummary,
    RCAdapter,
} from './RcAdapter.ts';
import { UserCancellationError } from './utils.ts';
import { Mutex } from 'async-mutex';
import { RawLocalState } from './State.tsx';
import { Session } from '@supabase/supabase-js';

const LAST_APP_USER_ID_KEY = 'rc_last_app_user_id';

Purchases.setLogLevel(LogLevel.Debug);

/** =========================================================
 *  Web (Stripe via RevenueCat Web Billing)
 *  =======================================================*/
export class RcWeb implements RCAdapter {
    private apiKey: string;
    private configuredFor?: string; // appUserId
    private mutex = new Mutex();

    constructor({ webKey, initialState }: { webKey: string; initialState: RawLocalState }) {
        if (!webKey) throw new Error('RevenueCat web key is required');
        this.apiKey = webKey;
        const session: Session | null = initialState.session ? (JSON.parse(initialState.session) as Session) : null;
        // We need the ID at this point just to preview the packages available.
        // This is not necessarily the final user ID we will use for purchases.
        // First we try to use the logged in user ID. Otherwise we fall back to
        // the last known user ID, even if the user is not actually signed in
        // with this ID right now.
        //
        // We do this to avoid "aliasing" in RevenueCat, which happens when
        // calling RC's logIn method _from_ an anonymous ID.  This associates
        // the anonymous ID with the user's account as an "alias", and there are
        // a finite number of aliases allowed per user in RC.
        // To avoid this, we try to reuse the last known user ID if possible.
        //
        // For now we just use localStorage because it's synchronous.  This was
        // the recommended approach from RC support. In the future we might want
        // to use the KeyValueStore instead, which will just require an async
        // init() method.
        const userId: string | undefined = session?.user.id ?? localStorage.getItem(LAST_APP_USER_ID_KEY) ?? undefined;
        const anonymousId = Purchases.generateRevenueCatAnonymousAppUserId();
        Purchases.configure({
            apiKey: this.apiKey,
            appUserId: userId ?? anonymousId,
        });
        this.configuredFor = anonymousId;
    }

    async hasEntitlement(appUserId: string, id: EntitlementId): Promise<boolean> {
        return await this.mutex.runExclusive(async () => {
            await this.ensureLoggedInAsUser(appUserId);
            const info = await Purchases.getSharedInstance().getCustomerInfo();
            return Boolean(info?.entitlements?.active?.[id]);
        });
    }

    async purchase(appUserId: string, pkg: PackageSummary) {
        return await this.mutex.runExclusive(async () => {
            try {
                await this.ensureLoggedInAsUser(appUserId);
                const info = await Purchases.getSharedInstance().purchase({ rcPackage: pkg._impl });
                return normalizeCustomer(info);
            } catch (e) {
                if (e instanceof PurchasesError && e.errorCode === ErrorCode.UserCancelledError) {
                    throw new UserCancellationError('User cancelled purchase');
                }
                console.error('Unexpected purchase error', e);
                throw e;
            }
        });
    }

    async getPackages(offeringId?: OfferingId) {
        return await this.mutex.runExclusive(async () => {
            const res = await Purchases.getSharedInstance().getOfferings();
            const current: Offering =
                offeringId && (res.all as any)?.[offeringId] ? (res.all as any)[offeringId] : res.current;
            if (!current) return [];
            return current.availablePackages.map((pkg) => {
                return {
                    title: pkg.webBillingProduct.title,
                    frequency: this.toFrequency(pkg.packageType),
                    price: {
                        amount: pkg.webBillingProduct.price.amountMicros * 1_000_000,
                        localized: pkg.webBillingProduct.price.formattedPrice,
                        currencyCode: pkg.webBillingProduct.price.currency,
                    },
                    id: pkg.webBillingProduct.identifier,
                    _impl: pkg,
                };
            });
        });
    }

    private async ensureLoggedInAsUser(appUserId: string): Promise<void> {
        if (this.configuredFor === appUserId) return;
        if (!appUserId) throw new Error('appUserId required for RevenueCat init (web).');
        await Purchases.getSharedInstance().changeUser(appUserId);
        this.configuredFor = appUserId;
        // Cache the appUserId so we don't need to use an anonymous ID again
        localStorage.setItem(LAST_APP_USER_ID_KEY, appUserId);
    }

    private toFrequency(packageType: PackageType): PackageFrequency {
        switch (packageType) {
            case PackageType.Monthly:
                return 'monthly';
            case PackageType.Annual:
                return 'annual';
            default:
                return 'unknown';
        }
    }

    async getSubscriptionManagementUrl(): Promise<string | null> {
        return (await Purchases.getSharedInstance().getCustomerInfo()).managementURL;
    }

    async restore() {
        // No op on web
        const info = await Purchases.getSharedInstance().getCustomerInfo();
        return normalizeCustomer(info);
    }

    async getCustomerState() {
        const info = await Purchases.getSharedInstance().getCustomerInfo();
        return normalizeCustomer(info);
    }

    async logOut() {
        return await this.mutex.runExclusive(async () => {
            const anonymousId = Purchases.generateRevenueCatAnonymousAppUserId();
            await Purchases.getSharedInstance().changeUser(anonymousId);
            this.configuredFor = anonymousId;
        });
    }
}
